import { forwardRef, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, ExternalLink, X } from "lucide-react";
import { useTour } from "./TourContext";
import type { StepPlacement, TourStep } from "./types";

/**
 * Renders the spotlight + tooltip for the active tour step. Mounts as a
 * portal so it isn't constrained by the layout grid of the host page.
 *
 * Positioning strategy:
 *   - "center" placement → tooltip centered, no spotlight (intro / outro).
 *   - explicit placement (top/bottom/left/right) → tooltip placed relative
 *     to the target's bounding rect, falling back to "bottom" if that
 *     would clip the viewport.
 *   - target missing → centered, with a "Element not found" hint visible
 *     in the dev console (silent in prod) so step-author bugs surface
 *     without breaking the user's session.
 *
 * Cross-page steps: when a step carries {@link TourStep.path} and the
 * router isn't already there, the overlay navigates BEFORE measuring the
 * target — that's what lets one tour walk the user across /chat → /saved
 * → /digests. The target lookup is retried for a short window after nav
 * so the spotlight catches up once the new page's DOM mounts.
 *
 * "Try it yourself" steps: when a step carries {@link TourStep.waitForEvent},
 * the Next button stays disabled until a window CustomEvent of that name
 * fires. The host page dispatches it when the user does the thing the
 * step is teaching.
 *
 * Accessibility: the tooltip is a focus-trapped dialog. Tab cycles within
 * it; focus lands on the tooltip when a step changes; an aria-live region
 * announces each step to screen readers.
 */
export function TourOverlay() {
  const { active, stepIndex, next, prev, cancel, stop } = useTour();
  const [rect, setRect] = useState<DOMRect | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const step: TourStep | null = active ? active.steps[stepIndex] ?? null : null;

  // Cross-page navigation: if the step belongs on a different route, push
  // there. Runs before the rect-measuring layout effect below (effects
  // fire top-to-bottom), so by the time we look for the target the route
  // change is already queued.
  useEffect(() => {
    if (!step?.path) return;
    if (location.pathname === step.path) return;
    navigate(step.path);
  }, [step, location.pathname, navigate]);

  // Recompute target rect when step changes OR when the window resizes /
  // scrolls (page content shifts → spotlight needs to follow). useLayoutEffect
  // (not useEffect) so the spotlight + tooltip render before paint, no flicker.
  useLayoutEffect(() => {
    if (!step) {
      setRect(null);
      return;
    }
    if (!step.target || step.placement === "center") {
      setRect(null);
      return;
    }
    // If the step navigates, the target may not be in the DOM yet — retry
    // a few times over ~1.5s before giving up. Covers route-transition lag.
    let cancelled = false;
    let attempts = 0;
    const cleanups: Array<() => void> = [];

    const tryResolve = () => {
      if (cancelled) return;
      const el = document.querySelector(step.target!);
      if (!el) {
        attempts += 1;
        if (attempts < 8) {
          const id = window.setTimeout(tryResolve, 200);
          cleanups.push(() => window.clearTimeout(id));
        } else {
          // eslint-disable-next-line no-console
          if (typeof process !== "undefined" && process.env?.NODE_ENV !== "production") {
            console.warn(`[tour] step target not found: ${step.target}`);
          }
          setRect(null);
        }
        return;
      }
      const compute = () => setRect(el.getBoundingClientRect());
      compute();
      // Scroll the target into view so it's visible inside the spotlight.
      el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
      const onResize = () => compute();
      window.addEventListener("resize", onResize);
      window.addEventListener("scroll", onResize, true);
      // Re-measure once after scroll-into-view animation likely completes.
      const t = window.setTimeout(compute, 350);
      cleanups.push(() => {
        window.removeEventListener("resize", onResize);
        window.removeEventListener("scroll", onResize, true);
        window.clearTimeout(t);
      });
    };

    tryResolve();
    return () => {
      cancelled = true;
      cleanups.forEach((fn) => fn());
    };
  }, [step, location.pathname]);

  if (!active || !step) return null;

  const isCentered = !step.target || step.placement === "center" || !rect;
  const isLast = stepIndex === active.steps.length - 1;
  const isFirst = stepIndex === 0;

  return createPortal(
    <StepDialog
      key={`${active.id}:${stepIndex}`}
      step={step}
      tourTitle={active.title}
      stepIndex={stepIndex}
      stepCount={active.steps.length}
      rect={rect}
      isCentered={isCentered}
      isFirst={isFirst}
      isLast={isLast}
      onNext={next}
      onPrev={prev}
      onCancel={cancel}
      onStop={stop}
    />,
    document.body,
  );
}

/**
 * One mounted step. Split out from {@link TourOverlay} and keyed on
 * `tourId:stepIndex` so React fully remounts it per step — that gives
 * each step a clean focus-trap lifecycle and a fresh waitForEvent latch.
 */
function StepDialog({
  step,
  tourTitle,
  stepIndex,
  stepCount,
  rect,
  isCentered,
  isFirst,
  isLast,
  onNext,
  onPrev,
  onCancel,
  onStop,
}: {
  step: TourStep;
  tourTitle: string;
  stepIndex: number;
  stepCount: number;
  rect: DOMRect | null;
  isCentered: boolean;
  isFirst: boolean;
  isLast: boolean;
  onNext: () => void;
  onPrev: () => void;
  onCancel: () => void;
  onStop: () => void;
}) {
  const tooltipRef = useRef<HTMLDivElement>(null);
  // "Try it yourself" latch — starts satisfied unless the step gates on an
  // event. Flips true when the named window CustomEvent fires.
  const [eventSatisfied, setEventSatisfied] = useState(!step.waitForEvent);

  useEffect(() => {
    if (!step.waitForEvent) return;
    setEventSatisfied(false);
    const handler = () => setEventSatisfied(true);
    window.addEventListener(step.waitForEvent, handler);
    return () => window.removeEventListener(step.waitForEvent!, handler);
  }, [step.waitForEvent]);

  const nextBlocked = Boolean(step.waitForEvent) && !eventSatisfied;

  // Focus trap: move focus into the tooltip on mount, cycle Tab within it.
  useEffect(() => {
    const node = tooltipRef.current;
    if (!node) return;
    const focusables = () =>
      Array.from(
        node.querySelectorAll<HTMLElement>(
          'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
        ),
      );
    // Defer initial focus a tick so layout has settled (avoids scroll jump).
    const id = window.setTimeout(() => {
      const f = focusables();
      (f[0] ?? node).focus();
    }, 0);
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const f = focusables();
      if (f.length === 0) return;
      const first = f[0];
      const last = f[f.length - 1];
      const activeEl = document.activeElement;
      if (e.shiftKey && (activeEl === first || activeEl === node)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && activeEl === last) {
        e.preventDefault();
        first.focus();
      }
    };
    node.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(id);
      node.removeEventListener("keydown", onKey);
    };
  }, []);

  const handleNext = useCallback(() => {
    if (nextBlocked) return;
    onNext();
  }, [nextBlocked, onNext]);

  return (
    <div className="fixed inset-0 z-[60]" role="dialog" aria-modal="true" aria-label={tourTitle}>
      {/* Screen-reader-only live region — announces each step on change. */}
      <div className="sr-only" aria-live="polite">
        {tourTitle}, step {stepIndex + 1} of {stepCount}: {step.title}. {step.body}
      </div>

      {/* Backdrop. Click-through is disabled — the user must use the controls. */}
      <div className="absolute inset-0 bg-slate-900/55 backdrop-blur-[1px]" onClick={onCancel} />

      {/* Spotlight cutout — a transparent rounded rectangle around the target,
          ringed with a soft outline so it pops against the dimmed backdrop. */}
      {!isCentered && rect && (
        <div
          className="pointer-events-none absolute rounded-lg ring-2 ring-white/95 shadow-[0_0_0_9999px_rgba(15,23,42,0.55)]"
          style={{
            top: rect.top - 6,
            left: rect.left - 6,
            width: rect.width + 12,
            height: rect.height + 12,
            transition: "all 220ms cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        />
      )}

      {/* Tooltip. */}
      <Tooltip ref={tooltipRef} rect={rect} placement={step.placement} isCentered={isCentered}>
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-2xs font-semibold uppercase tracking-wider text-emerald-600">
              {tourTitle} · Step {stepIndex + 1} of {stepCount}
            </div>
            <div className="mt-1 text-base font-semibold text-slate-900">{step.title}</div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Close tour"
            className="rounded p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Optional image / GIF above the body. */}
        {step.imageUrl && (
          <img
            src={step.imageUrl}
            alt=""
            className="mt-3 max-h-44 w-full rounded-lg border border-slate-200 object-cover"
            loading="lazy"
          />
        )}

        {/* Body — preserves \n line breaks from the step content. */}
        <div className="mt-1.5 whitespace-pre-line text-sm leading-relaxed text-slate-700">
          {step.body}
        </div>

        {/* Optional "Learn more" link. */}
        {step.docHref && step.docLabel && (
          <a
            href={step.docHref}
            target="_blank"
            rel="noreferrer"
            className="mt-2.5 inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-800 hover:underline"
          >
            {step.docLabel}
            <ExternalLink className="h-3 w-3" />
          </a>
        )}

        {/* Progress dots. */}
        <div className="mt-3.5 flex items-center gap-1" aria-hidden="true">
          {Array.from({ length: stepCount }).map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i === stepIndex ? "w-5 bg-emerald-600" : "w-1.5 bg-slate-300"
              }`}
            />
          ))}
        </div>

        <div className="mt-3 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={onStop}
            className="text-xs font-medium text-slate-500 hover:text-slate-800 hover:underline"
          >
            Skip tour
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onPrev}
              disabled={isFirst}
              className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ChevronLeft className="h-3.5 w-3.5" /> Back
            </button>
            <button
              type="button"
              onClick={handleNext}
              disabled={nextBlocked}
              aria-disabled={nextBlocked}
              title={nextBlocked ? step.waitHint ?? "Try it to continue" : undefined}
              className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500 disabled:shadow-none"
            >
              {nextBlocked ? (
                step.waitHint ?? "Try it to continue"
              ) : (
                <>
                  {isLast ? "Done" : "Next"}
                  {!isLast && <ChevronRight className="h-3.5 w-3.5" />}
                </>
              )}
            </button>
          </div>
        </div>
      </Tooltip>
    </div>
  );
}

const TOOLTIP_GAP = 14;

/** Tooltip width: 360 on roomy viewports, shrinks on narrow ones. */
function tooltipWidth(): number {
  if (typeof window === "undefined") return 360;
  return Math.min(360, window.innerWidth - 24);
}

const Tooltip = forwardRef<
  HTMLDivElement,
  {
    rect: DOMRect | null;
    placement?: StepPlacement;
    isCentered: boolean;
    children: React.ReactNode;
  }
>(function Tooltip({ rect, placement, isCentered, children }, ref) {
  const [width, setWidth] = useState(tooltipWidth);
  const [pos, setPos] = useState<{ top: number; left: number; arrow: StepPlacement | null }>({
    top: 0,
    left: 0,
    arrow: null,
  });

  // Track viewport width so the tooltip stays on-screen on small displays.
  useEffect(() => {
    const onResize = () => setWidth(tooltipWidth());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Recompute on every render where rect / placement / width changed. Cheap
  // math — single rect, no DOM reads beyond what useLayoutEffect did above.
  useEffect(() => {
    const tt = width;
    if (isCentered || !rect) {
      const w = window.innerWidth;
      const h = window.innerHeight;
      setPos({ top: Math.max(48, h / 2 - 140), left: Math.max(12, w / 2 - tt / 2), arrow: null });
      return;
    }
    const w = window.innerWidth;
    const h = window.innerHeight;
    const ttGap = TOOLTIP_GAP;
    const place = placement ?? "bottom";

    let top = 0;
    let left = 0;
    let arrow: StepPlacement | null = place;

    const tryBottom = () => ({ top: rect.bottom + ttGap, left: rect.left + rect.width / 2 - tt / 2 });
    const tryTop = () => ({ top: rect.top - ttGap - 180, left: rect.left + rect.width / 2 - tt / 2 });
    const tryRight = () => ({ top: rect.top + rect.height / 2 - 90, left: rect.right + ttGap });
    const tryLeft = () => ({ top: rect.top + rect.height / 2 - 90, left: rect.left - ttGap - tt });

    let p =
      place === "top"
        ? tryTop()
        : place === "right"
          ? tryRight()
          : place === "left"
            ? tryLeft()
            : tryBottom();

    // Fallback chain when the chosen placement clips: bottom → top → right → left → centered.
    const clipsHoriz = (lp: { left: number }) => lp.left < 8 || lp.left + tt > w - 8;
    const clipsVert = (tp: { top: number }) => tp.top < 8 || tp.top + 180 > h - 8;

    if (clipsHoriz(p) || clipsVert(p)) {
      const candidates: Array<[StepPlacement, { top: number; left: number }]> = [
        ["bottom", tryBottom()],
        ["top", tryTop()],
        ["right", tryRight()],
        ["left", tryLeft()],
      ];
      const ok = candidates.find(([, c]) => !clipsHoriz(c) && !clipsVert(c));
      if (ok) {
        arrow = ok[0];
        p = ok[1];
      } else {
        arrow = null;
        p = { top: Math.max(48, h / 2 - 140), left: Math.max(12, w / 2 - tt / 2) };
      }
    }

    top = Math.max(8, Math.min(h - 188, p.top));
    left = Math.max(8, Math.min(w - tt - 8, p.left));
    setPos({ top, left, arrow });
  }, [rect, placement, isCentered, width]);

  return (
    <div
      ref={ref}
      role="tooltip"
      tabIndex={-1}
      className="absolute rounded-xl border border-slate-200 bg-white p-4 shadow-xl outline-none ring-1 ring-slate-900/5"
      style={{ top: pos.top, left: pos.left, width, transition: "top 200ms, left 200ms" }}
    >
      {pos.arrow && <Arrow placement={pos.arrow} />}
      {children}
    </div>
  );
});

function Arrow({ placement }: { placement: StepPlacement }) {
  // CSS-borders-as-triangle. Color-matched to the tooltip background.
  const base = "absolute h-0 w-0";
  if (placement === "top") {
    return (
      <span
        className={`${base} bottom-[-8px] left-1/2 -translate-x-1/2 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-white`}
      />
    );
  }
  if (placement === "right") {
    return (
      <span
        className={`${base} left-[-8px] top-1/2 -translate-y-1/2 border-b-8 border-r-8 border-t-8 border-b-transparent border-r-white border-t-transparent`}
      />
    );
  }
  if (placement === "left") {
    return (
      <span
        className={`${base} right-[-8px] top-1/2 -translate-y-1/2 border-b-8 border-l-8 border-t-8 border-b-transparent border-l-white border-t-transparent`}
      />
    );
  }
  // default = bottom
  return (
    <span
      className={`${base} top-[-8px] left-1/2 -translate-x-1/2 border-b-8 border-l-8 border-r-8 border-b-white border-l-transparent border-r-transparent`}
    />
  );
}
